<template>
  <div>
    <textarea :id="instanceId"
              class="form-group form-control"
              ref="redactorx"
              :name="instanceId"
              :placeholder="placeholderText"
              :value="redactorText"
    />
    <b-progress v-show="progress>0"
                :value="progress"
                class="w-100 d-block">
    </b-progress>
  </div>
</template>

<script>
require("@/vendor/redactorx/redactorx.min.css");
require("@/assets/css/redactor.css");

import RedactorX from '../../vendor/redactorx/redactorx'
import '../../vendor/redactorx/plugins/imageresize/imageresize.min'
import '../../vendor/redactorx/plugins/imageposition/imageposition.min'
import '../../vendor/redactorx/plugins/filelink/filelink'
import '../../vendor/redactorx/plugins/beyondgrammar/beyondgrammar.min'
import '../../vendor/redactorx/plugins/clips/clips'
import '../../vendor/redactorx/plugins/emojis/emojis'
import EnvService from "../../env.settings";
import { USER_SUGGESTION_UI_OFFSET } from '../../utils/constants';

const {getEmojisList, getEmojisMap} = require('../../vendor/redactorx/plugins/emojis/data');

// --- Template variable pills ---------------------------------------------
// Pills are a frontend-only display layer. The wire format with the backend
// remains the DotLiquid `{{ variableName }}` text — convert pill ↔ text on
// load/save so the editor experience matches Hubspot et al. while the
// rendered email template syntax stays exactly the same.

function deriveVariableLabel(variable) {
  if (!variable) return '';
  // Strip "proof_" prefix from custom-field variables, then split
  // snake_case and camelCase into Title Case words.
  let body = variable.startsWith('proof_') ? variable.slice('proof_'.length) : variable;
  return body
    .replace(/[_]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

function buildVariablePillHtml(variable) {
  const label = deriveVariableLabel(variable);
  return '<span class="rx-variable-pill" contenteditable="false" data-variable="' +
    variable + '" title="{{ ' + variable + ' }}">' + label + '</span>';
}

// Wrap any `{{ variableName }}` substring in the given HTML with a pill span.
// Only matches `{{ ... }}` outputs — leaves `{% ... %}` Liquid control flow
// alone. Walks text nodes only so we never inject pill markup into HTML
// attribute values like `<img src="{{ proofThumbNailUri }}">`.
function wrapVariablesAsPills(html) {
  if (!html) return html;
  const container = document.createElement('div');
  container.innerHTML = html;

  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null);
  const textNodes = [];
  let n;
  while ((n = walker.nextNode())) textNodes.push(n);

  const re = /\{\{\s*([\w.]+)\s*\}\}/g;
  for (const t of textNodes) {
    if (!t.nodeValue || !/\{\{/.test(t.nodeValue)) continue;
    const frag = document.createDocumentFragment();
    let lastEnd = 0;
    let m;
    re.lastIndex = 0;
    while ((m = re.exec(t.nodeValue))) {
      if (m.index > lastEnd) {
        frag.appendChild(document.createTextNode(t.nodeValue.slice(lastEnd, m.index)));
      }
      const tmp = document.createElement('div');
      tmp.innerHTML = buildVariablePillHtml(m[1]);
      frag.appendChild(tmp.firstChild);
      lastEnd = m.index + m[0].length;
    }
    if (lastEnd === 0) continue; // no matches
    if (lastEnd < t.nodeValue.length) {
      frag.appendChild(document.createTextNode(t.nodeValue.slice(lastEnd)));
    }
    t.replaceWith(frag);
  }

  return container.innerHTML;
}

// Replace pill spans with their underlying `{{ variableName }}` text so the
// backend receives the canonical Liquid syntax.
function unwrapVariablePills(html) {
  if (!html || html.indexOf('data-variable=') === -1) return html;
  const container = document.createElement('div');
  container.innerHTML = html;
  container.querySelectorAll('[data-variable]').forEach((node) => {
    const variable = node.getAttribute('data-variable');
    if (!variable) return;
    node.replaceWith(document.createTextNode('{{ ' + variable + ' }}'));
  });
  return container.innerHTML;
}

export default {
  name: "redactor",
  redactor: false,
  props: {
    text: String,
    placeholder: {
      type: String,
      default: null,
    },
    commentApiUrl: {
      type: String,
      default: null,
    },
    publicProofId: {
      type: String,
      default: null,
    },
    curUsersTaggable: {
      type: Array,
      default: null,
    },
    showPreview: {
      type: Boolean,
      default: false,
    },
    showSource: {
      type: Boolean,
      default: true
    },
    autofocus: {
      type: Boolean,
      default: true
    },
    showTemplateVars: {
      type: Boolean,
      default: false
    },
    templateVariables: {
      type: Array,
      default: () => []
    }
  },
  watch: {
    text: function (newValue) {
      this.setTextBody(newValue);
    }
  },
  data() {
    return {
      redactorText: "",
      placeholderText: "",
      uploadUri: (this.$props.commentApiUrl !== undefined && this.$props.commentApiUrl) ? EnvService.instance.apiHost + this.$props.commentApiUrl : "",
      instanceId: this.$A.GetUUID(),
      loomSdkButton: null,
      progress: 0
    };
  },
  mounted: function () {
    this.initRedactor();
    this.redactorText = this.$props.text === undefined ? "" : this.$props.text;
    this.setTextBody(this.redactorText);
    this.placeholderText = this.$props.placeholder === null ?
        this.$A.LangService.getLabel('review_screen_labels', 'redactor_your_text_placeholder') :
        this.$props.placeholder;
  },
  beforeDestroy() {
    RedactorX(this.$refs.redactorx, "destroy")
    this.redactorx = null
    this.$parent.redactorx = null
  },
  methods: {
    getTextBody: function () {
      let textBody = this.redactorx.app.editor.getContent();
      textBody = unwrapVariablePills(textBody);
      textBody = textBody.replace("<a", "<a target='_blank'");
      // console.log("redactor text body:", textBody);
      return textBody;
    },
    setTextBody: function (newText) {
      const text = wrapVariablesAsPills(newText || "");
      this.redactorText = text;
      let self = this;
      setTimeout(() => {
        // Only restore focus if the user was already in this editor —
        // programmatic content updates must not steal focus from other fields.
        const hadFocus = self.$el && self.$el.contains(document.activeElement);
        self.redactorx && self.redactorx.app.editor.setContent({html: text});
        if (hadFocus) self.setFocusEnd();
      }, 250);
    },
    initRedactor: function () {
      let self = this;

      this.registerLoom(this);
      let plugins = ['imageresize', 'imageposition', 'filelink', 'beyondgrammar', 'loom', 'clips', 'emojis']; // , 'filemanager'

      if (this.$props.curUsersTaggable && this.$props.curUsersTaggable.length) {
        plugins.push('handle');
        this.registerHandle(this);
      }

      if (this.$props.showPreview) {
        plugins.push('preview');
        this.registerPreview(this);
      }

      if (this.$props.showTemplateVars && this.$props.templateVariables.length > 0) {
        plugins.push('templateVars');
        this.registerTemplateVars(this);
      }

      let app = RedactorX(this.$refs.redactorx, {
        plugins: plugins,
        embed: false,
        // handle: {},
        source: self.$props.showSource,
        fileUpload: self.uploadUri,
        context: true,
        editor: {
          focus: self.$props.autofocus,
          minHeight: "200px",
          pasteLinkTarget: "_blank",
          pasteImages: true,
        },
        image: {
          upload: self.uploadUri,
        },
        filelink: {
          upload: self.uploadUri,
          icon: '<i class="re-icon-file"></i>'
        },
        subscribe: {
          'app.start': function () {

            let mc = document.getElementsByClassName("modal-content");
            for (let i = 0; i < mc.length; mc++) {
              mc[0].removeAttribute("tabindex")
            }
            let mc2 = document.getElementsByClassName("modal-header")
            for (let i = 0; i < mc2.length; mc2++) {
              mc2[0].removeAttribute("tabindex")
            }

          },
          'editor.change': function (event) {
            let html = event.get('html');
            self.redactorText = html;
            self.$emit('input', html)
            return html
          },
          'editor.before.paste': async function (event) {
            if (event.params.e.clipboardData.files.length > 0) {
              let file = event.params.e.clipboardData.files[0];
              let progress = function (percent) {
                self.progress = percent;
                if (self.progress === 100) {
                  setTimeout(() => self.progress = 0, 1000);
                } // reset when completed
              }
              let insertDataUrl = function () {
                let reader = new FileReader();
                reader.onload = function (event) {
                  // console.log(event.target.result); // data url!
                  app.editor.insertContent({html: "<img src='" + event.target.result + "' />"});
                };
                reader.readAsDataURL(file);
              }

              let callbackUploaded = function (uploaded, resultData) {

                if (!uploaded) {
                  insertDataUrl();
                } else {

                  app.editor.insertContent({html: "<img src='" + resultData.data["file-1"].url + "' />"});
                }
              };
              if (self.$props.commentApiUrl === null) {
                await window.$A.ProofFileService.UploadMessageFileReturnUrl(
                    file,
                    self.$props.publicProofId,
                    callbackUploaded,
                    progress)
              } else {
                await window.$A.AnnotationService.UploadCommentFileReturnUrl(
                    file,
                    self.uploadUri,
                    callbackUploaded,
                    progress
                );
              }
            } else {
              // get pasted html
              let html = event.get('html');
              // return html back
              event.set('html', html);
            }
          }
        },
        clips: {
          icon: '<i class="re-icon-clips"></i>',
          items: getEmojisMap()
        },
        emojis: {
          icon: '<i class="re-icon-clips"></i>',
          items: getEmojisMap()
        }
      });
      this.redactorx = app;
      this.$parent.redactorx = app;

    },
    registerLoom: function (redactorComponent) {
      let self;
      RedactorX.add("plugin", "loom", {
        init: function () {
          if (redactorComponent.$A.LoomService.isLoomAvailable) {
            this.loomSdkButton = redactorComponent.$A.LoomService.configureSdkButton();
            this.loomSdkButton.on('recording-start', this._startRecording);
            this.loomSdkButton.on('cancel', this._endRecording);
            this.loomSdkButton.on('complete', this._endRecording);
            this.loomSdkButton.on('insert-click', this._createCommentWithLoom);
          }
          self = this;
        },

        start: function () {
          if (redactorComponent.$A.LoomService.supported) {
            this.app.toolbar.add('loom', {
              icon: '<i class="re-icon-loom"></i>',
              title: "Record with Loom",
              command: "loom.toggle",
              position: {after: 'add'}
            });
          }
        },

        toggle: function () {
          // return this._simulateLoomInLocalHost();
          this._recordWithLoom();
        },

        // _simulateLoomInLocalHost: function () {
        //   this._startRecording();
        //   let t = this;
        //   setTimeout(() => {
        //     t._createCommentWithLoom({sharedUrl: "https://www.google.com"});
        //   }, 1000);
        // },

        _recordWithLoom: function () {
          if (redactorComponent.$A.LoomService.supported && redactorComponent.$A.LoomService.isLoomAvailable) {
            self.loomSdkButton.openPreRecordPanel();
          }
        },

        _createCommentWithLoom: function (recording) {
          self._insertUrl(recording.sharedUrl);
          self._endRecording();
        },

        _startRecording: function () {
          let style = document.createElement("style");
          style.textContent = `
        .em-lns-1k46icp {
          bottom: 100px !important;
        }`;

          document.getElementById("loom-sdk-record-overlay-shadow-root-id").shadowRoot.appendChild(style);
          redactorComponent.$emit("onrecording", true);
        },

        _endRecording: function () {
          redactorComponent.$emit("onrecording", false);
        },

        _insertUrl: function (url) {
          let text = this.app.editor.getContent();
          if (text !== "") {
            this.app.editor.insertContent({html: "<br/>"});
          }
          this.app.editor.insertContent({html: `<br><a target='_blank' href='${url}'>${url}</a><br>`});
        },
      });
    },
    registerHandle: function (redactorComponent) {
      RedactorX.add('plugin', 'handle', {
        translations: {
          en: {
            "handle": {
              "handle": "Handle"
            }
          }
        },
        defaults: {
          url: false,
          start: 1,
          trigger: '@'
        },
        subscribe: {
          'editor.keydown': function (event) {
            this._listen(event);
          },
          'editor.keyup': function (event) {
            this._handle(event);
          }
        },
        init: function () {
          this.redactorComponent = redactorComponent;
        },
        start: function () {
          this.handleLen = this.opts.handle.start;
          this.app.toolbar.add("add_person", {
            title: "Add person",
            text: "@",
            icon: '<i class="re-icon-add-person"></i>',
            command: "handle.open",
            position: {after: 'loom'}
          });
        },
        open: function () {
          this.app.editor.insertContent({html: "<p>@</p>"});
          this._emit()
        },
        stop: function () {
          this._hide();
        },

        // private
        _handle: function (event) {
          var e = event.get('e');
          var key = e.which;
          var ctrl = e.ctrlKey || e.metaKey;
          var arrows = [37, 38, 39, 40];
          var ks = this.app.keycodes;

          if (key === ks.ESC) {
            this.app.selection.restore();
            return;
          }
          if (key === ks.DELETE || key === ks.SPACE || key === ks.SHIFT || ctrl || (arrows.indexOf(key) !== -1)) {
            return;
          }

          if (key === ks.BACKSPACE) {
            this.handleLen = this.handleLen - 2;
            if (this.handleLen <= this.opts.handle.start) {
              this._hide();
            }
          }

          this._emit();
        },
        _listen: function (event) {
          var e = event.get('e');
          var key = e.which;
          var ks = this.app.keycodes;

          // listen enter
          if (this._isShown() && key === ks.ENTER) {
            var $item = this._getActiveItem();
            if ($item.length === 0) {
              this._hideForce();
              return;
            } else {
              e.preventDefault();
              event.stop();
              this._replace(e, $item);
              return;
            }
          }

          // listen down / up
          if (this._isShown() && (key === 40 || key === 38)) {
            e.preventDefault();
            event.stop();

            var $item = this._getActiveItem();
            if ($item.length === 0) {
              var $first = this._getFirstItem();
              this._setActive($first);
            }
            // down
            else if (key === 40) {
              this._setNextActive($item);
            }
            // up
            else if (key === 38) {
              this._setPrevActive($item);
            }
          }
        },
        _getItems: function () {
          return this.$panel.find('.' + this.prefix + '-panel-item');
        },
        _getActiveItem: function () {
          return this._getItems().filter(function ($node) {
            return $node.hasClass('active');
          });
        },
        _getFirstItem: function () {
          return this._getItems().first();
        },
        _getLastItem: function () {
          return this._getItems().last();
        },
        _setActive: function ($el) {
          this._getItems().removeClass('active');
          $el.addClass('active');

          var itemHeight = $el.outerHeight();
          var itemTop = $el.position().top;
          var itemsScrollTop = this.$panel.scrollTop();
          var scrollTop = itemTop + itemHeight * 2;
          var itemsHeight = this.$panel.outerHeight();

          this.$panel.scrollTop(
              scrollTop > itemsScrollTop + itemsHeight ? scrollTop - itemsHeight :
                  itemTop - itemHeight < itemsScrollTop ? itemTop - itemHeight :
                      itemsScrollTop
          );
        },
        _setNextActive: function ($el) {
          var $next = $el.next();
          if ($next.length !== 0) {
            this._setActive($next);
          } else {
            var $first = this._getFirstItem();
            this._setActive($first);
          }
        },
        _setPrevActive: function ($el) {
          var $prev = $el.prev();
          if ($prev.length !== 0) {
            this._setActive($prev);
          } else {
            var $last = this._getLastItem();
            this._setActive($last);
          }
        },
        _emit: function () {
          var re = new RegExp('^' + this.opts.handle.trigger);
          this.handleStr = this.app.selection.getText('before', this.handleLen);

          // detect
          if (re.test(this.handleStr)) {
            this.handleStr = this.handleStr.replace(this.opts.handle.trigger, '');
            this.handleLen++;

            if ((this.handleLen - 1) > this.opts.handle.start) {
              this._load();
            }
          }
        },
        _isShown: function () {
          return (this.$panel && this.$panel.hasClass('open'));
        },
        _load: function () {
          let upper = function camelize(str) {
            return str.replace(/\b\w/g, (l) => l.toUpperCase());
          };
          if (
              this.redactorComponent.$props.curUsersTaggable !== undefined &&
              this.redactorComponent.$props.curUsersTaggable
          ) {
            // console.log(self.$props.curUsersTaggable);
            let o = {};

            // console.log(this.handleStr)
            this.redactorComponent.$props.curUsersTaggable
                .filter(
                    (a) =>
                        a.name.toLowerCase().indexOf(this.handleStr.toLowerCase()) >=
                        0
                )
                .forEach((a) => {
                  a.email = a.email?.replace("@", "&#64;");
                  o[a.type + "_" + a.id] = {
                    item:
                        "<b class='ashore_comment_mention' data-key='" +
                        a.type +
                        "_" +
                        a.id +
                        "'>" +
                        upper(a.name) +
                        " (" +
                        a.email +
                        ")</b>",
                    replacement:
                        "<b class='ashore_comment_mention' data-type='" +
                        a.type +
                        "' data-id='" +
                        a.id +
                        "' style='background-color:" +
                        "'>&#64;" +
                        upper(a.name) +
                        "</b>",
                  };
                });
            this._parse(o);
          }
        },
        _parse: function (json) {
          if (json === '' || (Array.isArray(json) && json.length === 0)) {
            if (this.$panel) this.$panel.remove();
            return;
          }
          var data = (typeof json === 'object') ? json : JSON.parse(json);

          this._build(data);
        },
        _build: function (data) {
          this.data = data;
          this.$panel = this.app.$body.find('.' + this.prefix + '-panel');

          if (this.$panel.length === 0) {
            this.$panel = this.dom('<div>').addClass(this.prefix + '-panel');
            this.app.$body.append(this.$panel);
          } else {
            this.$panel.html('');
          }

          // events
          this._stopEvents();
          this._startEvents();

          // data
          for (var key in data) {
            var $item = this.dom('<div>').addClass(this.prefix + '-panel-item');
            var $trigger = this.dom('<a>').attr('href', '#');
            $trigger.html(data[key].item);
            $trigger.attr('data-key', key);
            $trigger.on('click', this._replace.bind(this));

            $item.append($trigger);
            this.$panel.append($item);
          }

          // position
          var scrollTop = this.app.$doc.scrollTop();
          var pos = this.app.selection.getPosition();

        // Handled the @mention user suggestion responviness for mobile screen
          // Default position
          var top = pos.bottom + scrollTop;
          var left = pos.left;

          // Panel + viewport sizes
          var panelWidth = this.$panel.outerWidth();
          var panelHeight = this.$panel.outerHeight();
          var viewportWidth = $(window).width();
          var viewportHeight = $(window).height();

          // Adjust if overflowing right
          if (left + panelWidth > viewportWidth) {
            left = Math.max(0, viewportWidth - panelWidth - USER_SUGGESTION_UI_OFFSET);
          }

          // Adjust if overflowing bottom
          if (top + panelHeight > scrollTop + viewportHeight) {
            top = pos.top + scrollTop - panelHeight;
            if (top < 0) top = USER_SUGGESTION_UI_OFFSET; // safety
          }

          this.$panel.addClass('open');
          this.$panel.css({
            top: top + 'px',
            left: left + 'px'
          });

          this.app.selection.save();
        },
        _replace: function (e, $el) {
          e.preventDefault();
          e.stopPropagation();

          var $item;
          if ($el) {
            $item = $el.find('a');
          } else {
            $item = this.dom(e.target);
          }
          var key = $item.attr('data-key');
          var replacement = this.data[key].replacement;

          this.app.marker.insert('start');
          var marker = this.app.marker.find('start');
          if (marker === false) return;

          var $marker = this.dom(marker);
          var current = marker.previousSibling;

          var currentText = current.textContent;
          var re = new RegExp(this.opts.handle.trigger + this.handleStr + '$');

          currentText = currentText.replace(re, '');
          current.textContent = currentText;

          $marker.before(replacement);
          this.app.selection.restoreMarker();

          this._hide();
        },
        _reset: function () {
          this.handleStr = false;
          this.handleLen = this.opts.handle.start;
          this.$panel = false;
        },
        _hide: function (e) {
          var hidable = false;
          var key = (e && e.which);
          var ks = this.app.keycodes;

          if (!e) {
            hidable = true;
          } else if (e.type === 'click' || key === ks.ESC || key === ks.SPACE) {
            hidable = true;
          }

          if (hidable) {
            this._hideForce();
          }
        },
        _hideForce: function () {
          if (this.$panel) this.$panel.remove();
          this._reset();
          this._stopEvents();
        },
        _startEvents: function () {
          var name = 'click.' + this.prefix + '-plugin-handle keydown.' + this.prefix + '-plugin-handle';

          this.app.$doc.on(name, this._hide.bind(this));
          this.app.editor.getEditor().on(name, this._hide.bind(this));
        },
        _stopEvents: function () {
          var name = '.' + this.prefix + '-plugin-handle';

          this.app.$doc.off(name);
          this.app.editor.getEditor().off(name);
        }
      });
    },
    registerPreview: function (redactorComponent) {
      RedactorX.add("plugin", "preview", {
        init: function (app) {
        },
        start: function () {
          this.app.toolbar.add('preview', {
            icon: '<i class="re-icon-preview"></i>',
            title: "Preview",
            command: "preview.toggle",
          });
        },
        toggle: async function () {
          redactorComponent.$emit("preview");
        }
      });
    },
    registerTemplateVars: function (redactorComponent) {
      RedactorX.add('plugin', 'templateVars', {
        init: function () {
          this.redactorComponent = redactorComponent;
        },
        start: function () {
          this.app.toolbar.add('templateVars', {
            icon: '<svg width="16" height="16" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg"><path d="M4.5 2C3.67 2 3 2.67 3 3.5v9c0 .83.67 1.5 1.5 1.5h1V13H4.5a.5.5 0 01-.5-.5v-9a.5.5 0 01.5-.5H6V2H4.5zM10 2v1h1.5a.5.5 0 01.5.5v9a.5.5 0 01-.5.5H10v1h1.5c.83 0 1.5-.67 1.5-1.5v-9c0-.83-.67-1.5-1.5-1.5H10zM6.5 7.5a1 1 0 110 2 1 1 0 010-2zm3 0a1 1 0 110 2 1 1 0 010-2z"/></svg>',
            title: 'Insert Variable',
            command: 'templateVars.popup',
            position: { after: 'add' }
          });
        },
        popup: function (params, button) {
          const variables = this.redactorComponent.$props.templateVariables;
          if (!variables || variables.length === 0) {
            return;
          }

          const popup = this.app.popup.create('templateVars', {
            title: 'Insert Variable',
            width: '320px'
          });

          const $body = popup.getBody();
          const $container = this.dom('<div class="rx-template-vars-container">');

          for (const variable of variables) {
            const $item = this.dom('<a href="#" class="rx-template-vars-item">');
            $item.html(
              '<span class="rx-template-vars-name">{{ ' + variable.variable + ' }}</span>' +
              '<span class="rx-template-vars-desc">' + variable.text + '</span>'
            );
            $item.attr('data-variable', variable.variable);
            $item.on('click', this._insert.bind(this));
            $container.append($item);
          }

          $body.append($container);
          this.app.popup.open({ button: button });
        },
        _insert: function (e) {
          e.preventDefault();
          e.stopPropagation();

          this.app.popup.close();

          const $target = this.dom(e.target).closest('a');
          const variable = $target.attr('data-variable');

          this.app.editor.insertContent({ html: buildVariablePillHtml(variable) + '&nbsp;' });
        }
      });
    },
    setFocusEnd: function () {
      if (this.redactorx && this.redactorx.app) {
        this.redactorx.app.editor.setFocus('end');
      }
    }
  }
};
</script>

<style>
.rx-control, .rx-panel {
  z-index: 99999999 !important;
}

.rx-tooltip {
  z-index: 200000 !important;
}

.rx-popup {
  z-index: 200001 !important;
}

.rx-editor, .rx-editor [data-rx-type], .rx-editor a, .rx-editor figcaption, .rx-editor p, .rx-editor td, .rx-editor th {
  font-weight: 400 !important;
}

.rx-voice-label {
  display: none !important;
}

.progress {
  height: 3px !important;
  background-color: #F4F4F6;
  border-top: 0px !important;
  padding-top: 0px !important;
}

.rx-topbar {
  display: none !important;
}

.rx-button-icon {
  display: inherit !important;
}

.rx-container{
  border-radius: 6px;
}

/* Template Variables Plugin Styles */
.rx-template-vars-container {
  max-height: 280px;
  overflow-y: auto;
  padding: 8px 0;
}

.rx-template-vars-item {
  display: flex;
  flex-direction: column;
  padding: 10px 16px;
  text-decoration: none;
  color: inherit;
  transition: background-color 0.15s ease;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
}

.rx-template-vars-item:last-child {
  border-bottom: none;
}

.rx-template-vars-item:hover {
  background-color: #f4f4f6;
}

.rx-template-vars-name {
  font-family: monospace;
  font-size: 13px;
  color: #019256;
  font-weight: 500;
  margin-bottom: 4px;
}

.rx-template-vars-desc {
  font-size: 12px;
  color: #707073;
  line-height: 1.4;
}

/* Variable pill (in-editor display of `{{ variable }}` tokens) */
.rx-editor .rx-variable-pill {
  display: inline-block;
  padding: 1px 8px;
  margin: 0 1px;
  border-radius: 999px;
  background-color: #e6f4ed;
  color: #019256;
  font-size: 0.9em;
  font-weight: 500;
  line-height: 1.4;
  white-space: nowrap;
  cursor: default;
  user-select: all;
  vertical-align: baseline;
}

.rx-editor .rx-variable-pill:hover {
  background-color: #d4ecdf;
}
</style>
